import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  WxbAlert,
  WxbButton,
  WxbInput,
  WxbInputNumber,
  WxbModal,
  WxbSelect,
  WxbSpinner,
  WxbTextarea,
} from '../wxb-ui';
import TypedConditionEditor from './TypedConditionEditor';
import { formatAttributeOptionLabel } from './attributeCodeGeneration';
import {
  ACTION_ORIGIN_LABELS,
  EFFECT_TYPE_LABELS,
  formatTemporalExpression,
} from './equipmentStateCatalogModel';
import type {
  EquipmentStateAction,
  EquipmentStateActionOrigin,
  EquipmentStateAttribute,
  EquipmentStateEffect,
  EquipmentStateEffectType,
} from '../../types/prodEquipmentStateCatalog';
import type { OperationLibraryItem } from '../ProcessTemplateV2/types';

interface Props {
  open: boolean;
  readOnly?: boolean;
  initialAction: EquipmentStateAction | null;
  existingCodes: string[];
  attributes: EquipmentStateAttribute[];
  onCancel: () => void;
  onSave: (action: EquipmentStateAction) => void;
}

const emptyAction = (): EquipmentStateAction => ({
  actionCode: '',
  name: '',
  operationId: null,
  effectTiming: 'END',
  origin: 'DERIVABLE',
  description: null,
  guards: [],
  effects: [],
});

const clone = (action: EquipmentStateAction): EquipmentStateAction => (
  JSON.parse(JSON.stringify(action)) as EquipmentStateAction
);

const allowedEffectTypes = (attribute: EquipmentStateAttribute | undefined): EquipmentStateEffectType[] => {
  if (attribute?.kind === 'DISCRETE') return ['SET_STATE'];
  if (attribute?.kind === 'COUNTER') return ['INCREMENT', 'RESET_COUNTER'];
  if (attribute?.kind === 'CLOCK') return ['START_TIMER', 'CLEAR_TIMER'];
  return ['SET_STATE', 'INCREMENT', 'RESET_COUNTER', 'START_TIMER', 'CLEAR_TIMER'];
};

const defaultEffectType = (attribute: EquipmentStateAttribute | undefined): EquipmentStateEffectType => (
  allowedEffectTypes(attribute)[0] ?? 'SET_STATE'
);

const defaultStateValue = (attribute: EquipmentStateAttribute | undefined): string => (
  attribute?.temporalTransition?.validValueCode ?? attribute?.values[0]?.code ?? ''
);

type OperationLibraryLoadState = 'idle' | 'loading' | 'ready' | 'error';

const EFFECT_TIMING_OPTIONS = [
  { value: 'START', label: '操作开始时' },
  { value: 'END', label: '操作结束时' },
];

const operationLabel = (operation: OperationLibraryItem): string => (
  `${operation.operation_name} (${operation.operation_code})`
);

const temporalStateWriteSummary = (
  attribute: EquipmentStateAttribute | undefined,
  value: EquipmentStateEffect['value'],
  effectTimingLabel: string,
): string | null => {
  if (!attribute?.temporalTransition) return null;
  const transition = attribute.temporalTransition;
  if (value === transition.validValueCode) {
    const refreshText = transition.refreshRule.kind === 'RESET_ON_START_EVENT'
      ? '按刷新规则开始或重置'
      : transition.refreshRule.kind === 'ONE_SHOT'
        ? '仅在首个有效写入时开始'
        : '开始；后续按指定刷新事件重置';
    return `计划${effectTimingLabel}时状态变为 ${transition.validValueCode}；${refreshText} ${formatTemporalExpression(transition.expiryRule.expression)} 计时，到期自动变为 ${transition.expiredValueCode}。`;
  }
  if (value === transition.expiredValueCode) {
    return `计划${effectTimingLabel}时状态变为 ${transition.expiredValueCode}；立即失效并清除该周期的计时锚点。`;
  }
  return '周期状态只能写入其配置的有效值或到期值。';
};

const EquipmentActionModal: React.FC<Props> = ({
  open,
  readOnly = false,
  initialAction,
  existingCodes,
  attributes,
  onCancel,
  onSave,
}) => {
  const [draft, setDraft] = useState<EquipmentStateAction>(emptyAction());
  const [error, setError] = useState<string | null>(null);
  const [operationLibrary, setOperationLibrary] = useState<OperationLibraryItem[]>([]);
  const [operationLoadState, setOperationLoadState] = useState<OperationLibraryLoadState>('idle');

  const isNewAction = initialAction === null;
  const isHistoricalUnlinkedAction = Boolean(initialAction && !initialAction.operationId);
  const isOperationSelectionEditable = !readOnly && (isNewAction || isHistoricalUnlinkedAction);

  const loadOperationLibrary = useCallback(async () => {
    setOperationLoadState('loading');
    try {
      // Keep the editor shell testable without eagerly loading the legacy
      // axios client. The actual request remains the shared operation-library
      // service rather than a duplicate endpoint wrapper.
      const { processTemplateV2Api } = await import('../../services/processTemplateV2Api');
      const operations = await processTemplateV2Api.listOperationLibrary();
      setOperationLibrary(operations);
      setOperationLoadState('ready');
    } catch {
      setOperationLibrary([]);
      setOperationLoadState('error');
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setDraft(initialAction ? clone(initialAction) : emptyAction());
    setError(null);
  }, [initialAction, open]);

  useEffect(() => {
    if (!open || !isOperationSelectionEditable) return;
    void loadOperationLibrary();
  }, [isOperationSelectionEditable, loadOperationLibrary, open]);

  const effectTargetAttributes = useMemo(() => attributes, [attributes]);
  const attributeOptions = useMemo(() => effectTargetAttributes.map((attribute) => ({
    value: attribute.code,
    label: `${formatAttributeOptionLabel(attribute.name, attribute.code)}${attribute.temporalTransition ? ' · 周期状态' : ''}`,
  })), [effectTargetAttributes]);
  const operationOptions = useMemo(() => operationLibrary.map((operation) => ({
    value: String(operation.id),
    label: operationLabel(operation),
  })), [operationLibrary]);
  const selectedOperation = useMemo(() => operationLibrary.find((operation) => (
    String(operation.id) === draft.operationId
  )) ?? null, [draft.operationId, operationLibrary]);
  const effectTimingLabel = draft.effectTiming === 'START' ? '开始' : '结束';

  const selectOperation = (operationId: string) => {
    const operation = operationLibrary.find((item) => String(item.id) === operationId);
    if (!operation) return;
    setDraft((current) => ({
      ...current,
      operationId: String(operation.id),
      ...(isNewAction ? {
        actionCode: operation.operation_code.trim().toUpperCase(),
        name: operation.operation_name.trim(),
      } : {}),
    }));
  };

  const addEffect = () => {
    const attribute = effectTargetAttributes[0];
    const effectType = defaultEffectType(attribute);
    setDraft((current) => ({
      ...current,
      effects: [
        ...current.effects,
        {
          attributeCode: attribute?.code ?? '',
          effectType,
          ...(effectType === 'SET_STATE' ? { value: defaultStateValue(attribute) } : {}),
          ...(effectType === 'INCREMENT' ? { value: 1 } : {}),
        },
      ],
    }));
  };

  const updateEffect = (index: number, patch: Partial<EquipmentStateEffect>) => {
    setDraft((current) => ({
      ...current,
      effects: current.effects.map((effect, itemIndex) => (
        itemIndex === index ? { ...effect, ...patch } : effect
      )),
    }));
  };

  const setEffectAttribute = (index: number, attributeCode: string) => {
    const attribute = effectTargetAttributes.find((item) => item.code === attributeCode);
    const effectType = defaultEffectType(attribute);
    updateEffect(index, {
      attributeCode,
      effectType,
      value: effectType === 'SET_STATE' ? defaultStateValue(attribute)
        : effectType === 'INCREMENT' ? 1
          : undefined,
    });
  };

  const setEffectType = (index: number, effectType: EquipmentStateEffectType) => {
    const effect = draft.effects[index];
    const attribute = attributes.find((item) => item.code === effect.attributeCode);
    updateEffect(index, {
      effectType,
      value: effectType === 'SET_STATE' ? defaultStateValue(attribute)
        : effectType === 'INCREMENT' ? 1
          : undefined,
    });
  };

  const submit = () => {
    const actionCode = draft.actionCode.trim().toUpperCase();
    if (isOperationSelectionEditable && !draft.operationId) {
      setError(isNewAction
        ? '请从操作库选择要关联的操作。'
        : '请为历史动作选择对应的操作库来源。');
      return;
    }
    if (!actionCode || !draft.name.trim()) {
      setError('所选操作缺少操作编码或名称，暂不能创建状态动作。');
      return;
    }
    if (!initialAction && existingCodes.map((item) => item.toUpperCase()).includes(actionCode)) {
      setError(`动作编码 ${actionCode} 已存在。`);
      return;
    }
    const effectTargets = draft.effects.map((effect) => effect.attributeCode);
    if (new Set(effectTargets).size !== effectTargets.length) {
      setError('同一动作不能对同一属性重复定义效果；请合并为一个确定效果。');
      return;
    }
    if ([...draft.guards, ...draft.effects].some((item) => !item.attributeCode)) {
      setError('守卫和效果都必须选择属性。');
      return;
    }
    if (draft.effects.some((effect) => effect.effectType === 'SET_STATE' && !effect.value)) {
      setError('设置状态效果必须选择目标状态值。');
      return;
    }
    if (draft.effects.some((effect) => effect.effectType === 'INCREMENT'
      && (!Number.isFinite(Number(effect.value)) || Number(effect.value) === 0))) {
      setError('计数增量必须是非 0 有限数值。');
      return;
    }
    const invalidTemporalWrite = draft.effects.find((effect) => {
      if (effect.effectType !== 'SET_STATE') return false;
      const attribute = attributes.find((item) => item.code === effect.attributeCode);
      const transition = attribute?.temporalTransition;
      return Boolean(transition
        && effect.value !== transition.validValueCode
        && effect.value !== transition.expiredValueCode);
    });
    if (invalidTemporalWrite) {
      const attribute = attributes.find((item) => item.code === invalidTemporalWrite.attributeCode);
      const transition = attribute?.temporalTransition;
      setError(`周期状态 ${attribute?.name ?? invalidTemporalWrite.attributeCode} 只能写入有效值 ${transition?.validValueCode ?? '—'} 或到期值 ${transition?.expiredValueCode ?? '—'}。`);
      return;
    }
    if (!draft.operationId && draft.effects.some((effect) => (
      effect.effectType === 'SET_STATE'
      && Boolean(attributes.find((attribute) => attribute.code === effect.attributeCode)?.temporalTransition)
    ))) {
      setError('直接写入周期状态的动作必须关联操作库来源。');
      return;
    }
    onSave({
      ...draft,
      actionCode,
      name: draft.name.trim(),
      operationId: draft.operationId ? String(draft.operationId) : null,
      effectTiming: draft.effectTiming,
      description: draft.description?.trim() || null,
      effects: draft.effects.map((effect) => (
        effect.effectType === 'SET_STATE' || effect.effectType === 'INCREMENT'
          ? effect
          : { attributeCode: effect.attributeCode, effectType: effect.effectType }
      )),
    });
  };

  return (
    <WxbModal
      open={open}
      title={readOnly ? '查看状态动作' : initialAction ? '编辑状态动作' : '添加状态动作'}
      okText={initialAction ? '保存动作' : '添加动作'}
      onCancel={onCancel}
      onOk={submit}
      footer={readOnly ? (
        <div className="esc-drawer-actions">
          <WxbButton type="button" variant="ghost" onClick={onCancel}>关闭</WxbButton>
        </div>
      ) : undefined}
      width={900}
      destroyOnClose
    >
      <div className="esc-modal-stack">
        {error && <WxbAlert variant="error" title="动作尚不能保存">{error}</WxbAlert>}
        {isHistoricalUnlinkedAction && (
          <WxbAlert title="历史动作尚未关联操作库">
            请选择该动作在操作库中的实际来源。保存时只补充操作关联，不会改写稳定动作编码、名称快照或已有周期状态引用。
          </WxbAlert>
        )}
        {isOperationSelectionEditable && operationLoadState === 'error' && (
          <WxbAlert variant="error" title="操作库加载失败">
            无法读取可关联的操作。请重试，成功加载后才能{isNewAction ? '创建状态动作' : '完成来源关联'}。
            <div className="esc-alert-actions">
              <WxbButton type="button" size="sm" variant="secondary" onClick={() => void loadOperationLibrary()}>
                重试
              </WxbButton>
            </div>
          </WxbAlert>
        )}
        {isOperationSelectionEditable && operationLoadState === 'ready' && operationLibrary.length === 0 && (
          <WxbAlert title="操作库暂无可选操作">
            请先在“操作库”创建操作，再回到此处定义其前后设备状态变化。
          </WxbAlert>
        )}
        <div className="esc-form-grid esc-form-grid-three">
          {isOperationSelectionEditable ? (
            <WxbSelect
              label="关联操作（操作库）"
              aria-label="关联操作（操作库）"
              value={draft.operationId ?? undefined}
              disabled={operationLoadState !== 'ready' || operationLibrary.length === 0}
              loading={operationLoadState === 'loading'}
              showSearch
              optionFilterProp="label"
              options={operationOptions}
              placeholder={operationLoadState === 'loading' ? '正在加载操作库…' : '搜索并选择操作'}
              notFoundContent={operationLoadState === 'loading'
                ? <WxbSpinner size={20} tip="正在加载操作库…" />
                : '未找到匹配的操作'}
              onChange={(value) => selectOperation(String(value))}
            />
          ) : (
            <WxbInput
              label="关联操作（操作库）"
              value={draft.operationId
                ? `${draft.name || '未命名操作'} · #${draft.operationId}`
                : '未关联（历史动作）'}
              disabled
              helpText={draft.operationId
                ? '操作库关联在创建后保持稳定；操作编码和名称以下方快照为准。'
                : '该历史动作可继续维护前后状态，但不会自动绑定操作库。'}
            />
          )}
          <WxbSelect
            label="计划态状态变化生效时点"
            value={draft.effectTiming}
            disabled={readOnly}
            options={EFFECT_TIMING_OPTIONS}
            onChange={(value) => setDraft((current) => ({
              ...current,
              effectTiming: value as EquipmentStateAction['effectTiming'],
            }))}
          />
          <WxbSelect
            label="排产动作来源"
            disabled={readOnly}
            value={draft.origin}
            options={Object.entries(ACTION_ORIGIN_LABELS).map(([value, label]) => ({ value, label }))}
            onChange={(value) => setDraft((current) => ({
              ...current,
              origin: value as EquipmentStateActionOrigin,
            }))}
          />
        </div>
        {isOperationSelectionEditable && operationLoadState === 'loading' && (
          <div className="esc-inline-empty">
            <WxbSpinner size={20} tip="正在读取操作库，请稍候…" />
          </div>
        )}
        <div className="esc-form-grid">
          <WxbInput
            label="稳定动作编码"
            value={draft.actionCode}
            disabled
            placeholder={isHistoricalUnlinkedAction ? '保留历史动作编码' : '选择操作后自动带入'}
            helpText={isHistoricalUnlinkedAction
              ? '历史稳定动作编码保持不变；补充操作库来源不会改写已有引用。'
              : selectedOperation
                ? '由已选操作的编码创建；保存后作为类别内稳定引用键。'
                : '动作编码由关联操作自动带入，不支持手动填写。'}
          />
          <WxbInput
            label="动作名称"
            value={draft.name}
            disabled
            placeholder={isHistoricalUnlinkedAction ? '保留历史动作名称' : '选择操作后自动带入'}
            helpText={isHistoricalUnlinkedAction
              ? '历史动作名称快照保持不变；补充操作库来源不会改写已有引用。'
              : selectedOperation
                ? '由已选操作的名称创建；保存后保留当前快照。'
                : '动作名称由关联操作自动带入，不支持手动填写。'}
          />
        </div>
        <WxbAlert title="计划态状态投影">
          APS 会以计划操作为真值投影未来设备状态：选择“操作开始时”会在开始时生效，选择“操作结束时”会在结束时生效；不等待人工完成确认。
        </WxbAlert>
        <WxbTextarea
          label="动作说明"
          disabled={readOnly}
          value={draft.description ?? ''}
          placeholder="说明操作的业务边界和状态转换意图。"
          onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
        />

        <div className="esc-subsection">
          <div className="esc-subsection-header">
            <div>
              <h3>发生前状态条件</h3>
              <p>在计划操作开始前，设备必须满足的状态条件；所有条件按 AND 组合。</p>
            </div>
          </div>
          <TypedConditionEditor
            attributes={attributes}
            conditions={draft.guards}
            disabled={readOnly}
            emptyText="无守卫条件，动作始终可尝试。"
            onChange={(guards) => setDraft((current) => ({ ...current, guards }))}
          />
        </div>

        <div className="esc-subsection">
          <div className="esc-subsection-header">
            <div>
              <h3>发生后状态变化</h3>
              <p>计划操作{effectTimingLabel}时原子投影的属性变化。周期状态直接写入有效值或到期值：有效值按属性刷新规则开始或重置计时，到期值立即失效并清除计时。</p>
            </div>
            {!readOnly && (
              <WxbButton
                type="button"
                size="sm"
                variant="secondary"
                disabled={effectTargetAttributes.length === 0}
                onClick={addEffect}
              >
                添加效果
              </WxbButton>
            )}
          </div>
          {draft.effects.some((effect) => effect.effectType === 'ACTIVATE_TEMPORAL_STATE') && (
            <WxbAlert title="包含旧版周期激活效果">
              该草稿仍可查看旧版“激活周期状态”定义。保存类别草稿时，系统会将其迁移为对有效值的普通状态写入；新动作不再提供这种效果。
            </WxbAlert>
          )}
          {draft.effects.length === 0 && (
            <div className="esc-inline-empty">尚未添加发生后状态变化。</div>
          )}
          <div className="esc-effect-list">
            {draft.effects.map((effect, index) => {
              const attribute = attributes.find((item) => item.code === effect.attributeCode);
              const effectTypes = allowedEffectTypes(attribute);
              const visibleEffectTypes = effectTypes.includes(effect.effectType)
                ? effectTypes
                : [effect.effectType, ...effectTypes];
              const stateValueOptions = (attribute?.temporalTransition
                ? attribute.values.filter((value) => (
                  value.code === attribute.temporalTransition?.validValueCode
                  || value.code === attribute.temporalTransition?.expiredValueCode
                ))
                : attribute?.values ?? []
              ).map((value) => ({
                value: value.code,
                label: `${value.name} (${value.code})`,
              }));
              return (
                <div className="esc-effect-row" key={`${effect.attributeCode}-${index}`}>
                  <WxbSelect
                    aria-label={`效果 ${index + 1} 属性`}
                    value={effect.attributeCode || undefined}
                    disabled={readOnly}
                    options={attributeOptions}
                    placeholder="选择属性"
                    onChange={(value) => setEffectAttribute(index, String(value))}
                  />
                  <WxbSelect
                    aria-label={`效果 ${index + 1} 类型`}
                    value={effect.effectType}
                    disabled={readOnly}
                    options={visibleEffectTypes.map((value) => ({
                      value,
                      label: value === 'ACTIVATE_TEMPORAL_STATE'
                        ? '激活周期状态（旧版）'
                        : EFFECT_TYPE_LABELS[value],
                    }))}
                    onChange={(value) => setEffectType(index, value as EquipmentStateEffectType)}
                  />
                  <div className="esc-effect-value">
                    {effect.effectType === 'SET_STATE' ? (
                      <div className="esc-modal-stack">
                        <WxbSelect
                          aria-label={`效果 ${index + 1} 状态值`}
                          value={typeof effect.value === 'string' ? effect.value : undefined}
                          disabled={readOnly}
                          options={stateValueOptions}
                          placeholder="目标状态"
                          onChange={(value) => updateEffect(index, { value: String(value) })}
                        />
                        {attribute?.temporalTransition && (
                          <div className="esc-condition-no-value">
                            {temporalStateWriteSummary(attribute, effect.value, effectTimingLabel)}
                          </div>
                        )}
                      </div>
                    ) : effect.effectType === 'INCREMENT' ? (
                      <WxbInputNumber
                        aria-label={`效果 ${index + 1} 增量`}
                        value={typeof effect.value === 'number' ? effect.value : null}
                        disabled={readOnly}
                        placeholder="非 0 增量"
                        onChange={(value) => updateEffect(index, {
                          value: typeof value === 'number' ? value : null,
                        })}
                      />
                    ) : effect.effectType === 'ACTIVATE_TEMPORAL_STATE' ? (
                      <span className="esc-condition-no-value">
                        旧版效果；保存类别草稿时会迁移为写入 {attribute?.temporalTransition?.validValueCode ?? '配置的有效值'}。
                      </span>
                    ) : (
                      <span className="esc-condition-no-value">由属性定义决定</span>
                    )}
                  </div>
                  {!readOnly && (
                    <WxbButton
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`删除效果 ${index + 1}`}
                      onClick={() => setDraft((current) => ({
                        ...current,
                        effects: current.effects.filter((_, itemIndex) => itemIndex !== index),
                      }))}
                    >
                      删除
                    </WxbButton>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </WxbModal>
  );
};

export default EquipmentActionModal;
